/**
 * Exercices PRÉ-CRÉÉS — ni générés par l'IA, ni corrigés par Gemini.
 *
 * Deux usages :
 * 1. `PREBUILT_EXERCISES` + `getPrebuiltExercisesForNode()` : catalogue léger consommé par le
 *    parcours d'apprentissage (bouton d'entrée dans le panneau du nœud) et par le backend
 *    (`POST /api/exercises/:id/sim-result` crédite `nodeId` et `kpCodes`).
 *    Point d'entrée d'un exercice : `/exercise/<id>?learningNodeId=<nodeId>`.
 * 2. `PREBUILT_EXERCISE_CONTENTS` : contenu inséré dans la table `exercises` au démarrage du backend
 *    (`services/prebuilt-exercises.ts`, idempotent, versionné par `contentVersion`).
 *
 * Le texte du simulateur `Time.deltaTime` est la copie de `frontend/src/simulator/templates.ts`
 * (`DELTATIME_EXERCISE_SPEC`) — le backend ne peut pas importer le frontend. Un test vitest
 * (`frontend/src/components/simulator/__tests__/prebuiltContent.test.ts`) garantit que les deux
 * copies restent identiques : modifier l'une sans l'autre casse le test.
 */

export interface PrebuiltExercise {
  id: string;
  learningMode: 'unity_csharp' | 'python_js' | 'godot';
  nodeId: string;
  kpCodes: string[];
  title: string;
  shortLabel: string;
  kind: 'interactive_sim';
}

export const PREBUILT_EXERCISES: PrebuiltExercise[] = [
  {
    id: 'sim_deltatime_v1',
    learningMode: 'unity_csharp',
    nodeId: 'monobehaviour_lifecycle',
    kpCodes: ['time_deltatime_frame_independence', 'update_without_deltatime_pitfall', 'lifecycle_update'],
    title: 'Simulateur : Time.deltaTime',
    shortLabel: 'Simulateur deltaTime',
    kind: 'interactive_sim',
  },
];

export const getPrebuiltExercisesForNode = (mode: string, nodeId: string): PrebuiltExercise[] =>
  PREBUILT_EXERCISES.filter((e) => e.learningMode === mode && e.nodeId === nodeId);

export const getPrebuiltExercise = (id: string): PrebuiltExercise | undefined =>
  PREBUILT_EXERCISES.find((e) => e.id === id);

/** Contenu de la ligne `exercises` (colonnes de la table, en camelCase). */
export interface PrebuiltExerciseContent {
  id: string;
  /** Incrémenter à chaque modification du contenu : le seed met la ligne à jour au démarrage. */
  contentVersion: number;
  title: string;
  /** Résumé court (colonne `description`, affiché dans l'historique et les cartes). */
  description: string;
  /** Énoncé complet en markdown (colonne `prompt`, affiché sur la page). */
  prompt: string;
  concepts: string[];
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  type: 'interactive_sim';
  learningMode: 'unity_csharp' | 'python_js' | 'godot';
  programmingLanguage: 'csharp' | 'python' | 'javascript' | 'gdscript';
  startingCode: string;
  expectedSolution: string;
  hints: string[];
  learningOutcomes: string[];
  successCriteria: string[];
  commonMistakes: { mistake: string; explanation: string; example?: string }[];
  estimatedTimeMinutes: number;
  createdBy: 'system';
}

export const SIM_DELTATIME_STARTER_CODE = `using UnityEngine;

// Déplace le vaisseau avec les flèches (ou ZQSD / WASD).
// Objectif : la MÊME vitesse à 5, 30 et 60 images par seconde.
public class ShipController : MonoBehaviour
{
    [SerializeField] float speed = 5f; // en unités par seconde

    void Update()
    {
        // TODO 1 : lire les entrées (-1, 0 ou 1 sur chaque axe)
        // float h = Input.GetAxisRaw("Horizontal");
        // float v = Input.GetAxisRaw("Vertical");

        // TODO 2 : construire la direction à partir de h et v
        //          (un Vector3, normalisé pour que la diagonale ne soit pas plus rapide)

        // TODO 3 : déplacer transform.position à la vitesse "speed"
        //          -> la même vitesse quelle que soit la cadence d'images !
    }
}
`;

export const SIM_DELTATIME_REFERENCE_SOLUTION = `using UnityEngine;

public class ShipController : MonoBehaviour
{
    [SerializeField] float speed = 5f;

    void Update()
    {
        float h = Input.GetAxisRaw("Horizontal");
        float v = Input.GetAxisRaw("Vertical");
        Vector3 direction = new Vector3(h, v, 0f).normalized;
        transform.position += direction * speed * Time.deltaTime;
    }
}
`;

export const SIM_DELTATIME_CONTENT: PrebuiltExerciseContent = {
  id: 'sim_deltatime_v1',
  contentVersion: 1,
  title: 'Simulateur : la même vitesse à 5, 30 et 60 FPS (Time.deltaTime)',
  description:
    'Programme le déplacement d\'un vaisseau, appuie sur **Play** et vérifie qu\'il avance à la même vitesse à 5, 30 et 60 images par seconde grâce à **Time.deltaTime**.',
  prompt: `## Le problème

Ton vaisseau doit se déplacer avec les flèches (ou ZQSD / WASD) à **5 unités par seconde**.

Unity appelle \`Update()\` **une fois par image**. Sur une machine rapide, c'est 60 fois par seconde ; sur une machine lente ou un téléphone, parfois 5. Si tu avances de \`speed\` **à chaque image**, ton vaisseau va 12 fois plus vite à 60 FPS qu'à 5 FPS — et ton jeu devient injouable selon l'ordinateur.

## Ce que tu dois faire

1. Lire les entrées : \`Input.GetAxisRaw("Horizontal")\` et \`Input.GetAxisRaw("Vertical")\` renvoient \`-1\`, \`0\` ou \`1\`.
2. Construire une direction avec \`new Vector3(h, v, 0f)\`, puis la **normaliser** (\`.normalized\`) pour que la diagonale ne soit pas plus rapide.
3. Déplacer \`transform.position\` à la vitesse \`speed\`, **en unités par seconde**, quelle que soit la cadence d'images.

## Pour tester

Appuie sur **Play**, bouge le vaisseau, puis change la cadence (**5 / 30 / 60 FPS**) et observe le HUD « distance par seconde ». Quand le nombre reste le même aux trois cadences, tu as compris \`Time.deltaTime\`.

> Le simulateur ne comprend qu'une petite partie de C#/Unity (celle dont tu as besoin ici). Si tu utilises autre chose, il te le dira et tu pourras demander pourquoi au tuteur.`,
  concepts: ['monobehaviour_lifecycle', 'vector_math'],
  difficulty: 'beginner',
  type: 'interactive_sim',
  learningMode: 'unity_csharp',
  programmingLanguage: 'csharp',
  startingCode: SIM_DELTATIME_STARTER_CODE,
  expectedSolution: SIM_DELTATIME_REFERENCE_SOLUTION,
  hints: [
    'Time.deltaTime est le temps écoulé depuis l\'image précédente, en secondes : environ 0.2 à 5 FPS, 0.0167 à 60 FPS.',
    'Vitesse (u/s) × temps (s) = distance (u). Que faut-il multiplier par quoi ?',
    'Compare le HUD « distance / s » à 5 FPS et à 60 FPS : si le rapport vaut 12, il manque quelque chose.',
    'La ligne finale tient en une instruction : transform.position += direction * speed * Time.deltaTime;',
  ],
  learningOutcomes: [
    'Comprendre que Update() est appelé une fois par image et que la cadence varie selon la machine.',
    'Utiliser Time.deltaTime pour exprimer un déplacement en unités par seconde.',
    'Lire des entrées clavier avec Input.GetAxisRaw et construire un Vector3 de direction normalisé.',
    'Modifier transform.position (et savoir pourquoi transform.position.x = … est refusé par C#).',
  ],
  successCriteria: [
    'Le vaisseau se déplace quand on appuie sur les flèches (ou ZQSD / WASD).',
    'La distance parcourue par seconde est identique (±10 %) à 5, 30 et 60 FPS.',
    'La vitesse mesurée vaut ≈ 5 unités par seconde (la valeur du champ speed).',
    'En diagonale, la vitesse reste 5 u/s (direction normalisée), pas 7.',
  ],
  commonMistakes: [
    {
      mistake: 'Oublier Time.deltaTime : transform.position += direction * speed;',
      explanation: 'Le vaisseau avance de « speed » unités à CHAQUE image : 12 fois plus vite à 60 FPS qu\'à 5 FPS.',
      example: 'transform.position += direction * speed * Time.deltaTime;',
    },
    {
      mistake: 'Ne pas normaliser la direction en diagonale',
      explanation: 'Un Vector3(1, 1, 0) a une longueur de 1,41 : en diagonale le vaisseau va 41 % plus vite.',
      example: 'Vector3 direction = new Vector3(h, v, 0f).normalized;',
    },
  ],
  estimatedTimeMinutes: 20,
  createdBy: 'system',
};

export const PREBUILT_EXERCISE_CONTENTS: PrebuiltExerciseContent[] = [SIM_DELTATIME_CONTENT];
